import type { NotificationFeedLiveEvent, NotificationFeedReady } from '@sharedlib/notification-contract';
import {
  notificationCursor,
  notificationGeneration,
  notificationKeys,
  notificationRecord,
  notificationUuid,
  parseNotificationLiveEvent,
  parseNotificationReady,
} from './notification-feed.validation';

// Server and renderer clocks can differ; Gateway owns the authoritative quiet/live fence.
const SERVER_CLOCK_SKEW_MS = 5_000;

/** A socket belongs to the host, never to the native coordinator's source subscription. */
export interface HostedNotificationSocket {
  on(event: string, listener: (value: unknown) => void): void;
  emit(event: 'feed.sync' | 'feed.ack', data: object): void;
  disconnect(): void;
}

interface Recipient {
  phase: 'quiet' | 'live';
  syncBegan: number;
  pageEvents: number;
  deliveries: Set<string>;
}

export interface NotificationFeedClientOptions {
  socket: HostedNotificationSocket;
  cursors: Map<string, string>;
  ready(ready: NotificationFeedReady): Promise<boolean>;
  event(event: NotificationFeedLiveEvent): void;
  removed(accountId: string): void;
  unavailable(): void;
  now?: () => number;
}

/** Strict quiet/live boundary. No recovery item is ever offered to a presenter. */
export class NotificationFeedClient {
  private closed = false;
  private revision = 0;
  private generation = 0;
  private connectionId: string | null = null;
  private readonly recipients = new Map<string, Recipient>();
  private readonly now: () => number;

  public constructor(private readonly options: NotificationFeedClientOptions) {
    this.now = options.now ?? Date.now;
    options.socket.on('feed.ready', (value) => void this.ready(value).catch(() => this.fail()));
    options.socket.on('feed.synchronized', (value) => this.checkpoint(value, false));
    options.socket.on('feed.progress', (value) => this.checkpoint(value, true));
    options.socket.on('feed.event', (value) => this.live(value));
    options.socket.on('feed.reset-required', (value) => this.reset(value));
    options.socket.on('feed.scope-removed', (value) => this.scopeRemoved(value));
    options.socket.on('feed.unavailable', () => this.fail());
    options.socket.on('connect_error', () => this.fail());
    options.socket.on('disconnect', () => this.fail());
  }

  public close(): void {
    if (this.closed) return;
    this.closed = true;
    this.revision += 1;
    this.recipients.clear();
    this.options.socket.disconnect();
  }

  private fail(): void {
    if (this.closed) return;
    this.close();
    this.options.unavailable();
  }

  private async ready(value: unknown): Promise<void> {
    if (this.closed) return;
    const ready = parseNotificationReady(value);
    if (
      !ready ||
      Date.parse(ready.leaseExpiresAt) <= this.now() ||
      Date.parse(ready.leaseExpiresAt) > this.now() + 300_000 + SERVER_CLOCK_SKEW_MS ||
      (this.connectionId !== null && (ready.connectionId !== this.connectionId || ready.generation <= this.generation))
    )
      return this.fail();
    const revision = ++this.revision;
    this.generation = ready.generation;
    this.connectionId = ready.connectionId;
    this.recipients.clear();
    for (const accountId of this.options.cursors.keys()) {
      if (!ready.recipients.some((recipient) => recipient.accountId === accountId)) {
        this.options.cursors.delete(accountId);
        this.options.removed(accountId);
      }
    }
    const started = await this.options.ready(ready);
    if (this.closed || revision !== this.revision) return;
    if (!started) return this.fail();
    for (const { accountId } of ready.recipients) {
      this.recipients.set(accountId, { phase: 'quiet', syncBegan: this.now(), pageEvents: 0, deliveries: new Set() });
      const cursor = this.options.cursors.get(accountId);
      this.options.socket.emit('feed.sync', {
        version: 1,
        generation: ready.generation,
        accountId,
        ...(cursor ? { cursor } : {}),
      });
    }
  }

  private checkpoint(value: unknown, live: boolean): void {
    if (this.closed) return;
    if (
      !notificationRecord(value) ||
      !notificationKeys(
        value,
        live
          ? ['version', 'generation', 'accountId', 'mode', 'cursor']
          : ['version', 'generation', 'accountId', 'cursor'],
      ) ||
      value['version'] !== 1 ||
      value['generation'] !== this.generation ||
      !notificationUuid(value['accountId']) ||
      !notificationCursor(value['cursor']) ||
      (live && value['mode'] !== 'live')
    )
      return this.fail();
    const recipient = this.recipients.get(value['accountId']);
    if (!recipient || recipient.phase !== (live ? 'live' : 'quiet')) return this.fail();
    this.options.cursors.set(value['accountId'], value['cursor']);
    recipient.phase = 'live';
    recipient.pageEvents = 0;
    recipient.deliveries.clear();
    // Checkpoint is a wire receipt, not evidence that a native banner was displayed.
    this.options.socket.emit('feed.ack', {
      version: 1,
      generation: this.generation,
      accountId: value['accountId'],
      cursor: value['cursor'],
    });
  }

  private live(value: unknown): void {
    if (this.closed) return;
    const event = parseNotificationLiveEvent(value);
    const recipient = event ? this.recipients.get(event.accountId) : undefined;
    if (
      !event ||
      event.generation !== this.generation ||
      !recipient ||
      recipient.phase !== 'live' ||
      recipient.pageEvents >= 10 ||
      recipient.deliveries.has(event.deliveryId)
    )
      return this.fail();
    recipient.pageEvents += 1;
    recipient.deliveries.add(event.deliveryId);
    const now = this.now();
    const occurred = Date.parse(event.occurredAt);
    if (
      occurred <= recipient.syncBegan - SERVER_CLOCK_SKEW_MS ||
      occurred > now + SERVER_CLOCK_SKEW_MS ||
      now - occurred >= 600_000 ||
      Date.parse(event.expiresAt) <= now ||
      Date.parse(event.expiresAt) <= occurred
    )
      return;
    this.options.event(event);
  }

  private reset(value: unknown): void {
    if (this.closed) return;
    if (
      !notificationRecord(value) ||
      !notificationKeys(value, ['version', 'generation', 'accountId', 'reason']) ||
      value['version'] !== 1 ||
      value['generation'] !== this.generation ||
      !notificationUuid(value['accountId']) ||
      value['reason'] !== 'reset-required' ||
      !this.recipients.has(value['accountId'])
    )
      return this.fail();
    this.options.cursors.delete(value['accountId']);
    // A fresh connection establishes a quiet fence; never replay a reset as live.
    this.fail();
  }

  private scopeRemoved(value: unknown): void {
    if (this.closed) return;
    if (
      !notificationRecord(value) ||
      !notificationKeys(value, ['version', 'generation', 'accountIds']) ||
      value['version'] !== 1 ||
      !notificationGeneration(value['generation']) ||
      value['generation'] < this.generation ||
      !Array.isArray(value['accountIds']) ||
      value['accountIds'].length < 1 ||
      value['accountIds'].length > 100
    )
      return this.fail();
    for (const accountId of value['accountIds']) {
      if (!notificationUuid(accountId) || !this.recipients.has(accountId)) return this.fail();
      this.options.cursors.delete(accountId);
      this.options.removed(accountId);
      this.recipients.delete(accountId);
    }
    this.fail();
  }
}
